import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LeadFormInput } from "@fleetlease/shared";
import { useState } from "react";
import { apiFetch } from "../lib/api";
import { Icon } from "../app/icons";

const Star = ({ s = 15 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="#ffb400" stroke="none"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>
);

const FLEET = [
  { name: "Honda Accord", meta: "2022 · Hybrid · 18k mi", price: "$650", rating: "4.9", img: "honda-accord.png", prompt: "Silver Honda Accord hybrid sedan, front 3/4 angle, clean city street, soft daylight, premium catalog photography" },
  { name: "Tesla Model 3", meta: "2023 · Electric · 12k mi", price: "$900", rating: "5.0", img: "tesla-model-3.png", prompt: "White Tesla Model 3, front 3/4 angle, bright minimalist street, premium EV photography" },
  { name: "Hyundai Ioniq 5", meta: "2024 · Electric · 4k mi", price: "$780", rating: "4.8", img: "hyundai-ioniq-5.png", prompt: "Matte grey Hyundai Ioniq 5, front 3/4 angle, modern architecture backdrop, soft daylight" },
];

const HERO_PROMPT = "Editorial front 3/4 photo of a clean silver hybrid sedan on a sunlit modern city street, glossy paint, shallow depth of field, bright airy premium automotive photography, no text";
const LIFESTYLE_PROMPT = "Smiling young professional taking car keys outside a modern building, warm golden-hour light, premium lifestyle photography, shallow depth of field, no text";

const STEPS = [
  ["Choose your car", "Browse the available fleet and pick the vehicle that fits your life."],
  ["Submit your info", "Send your details and license — it takes a couple of minutes, online."],
  ["Sign & pay monthly", "Simple monthly billing. Insurance and maintenance are handled for you."],
  ["Drive", "Pick up your car and go. Extend month-to-month whenever you like."],
];

const WHY: [string, string, string][] = [
  ["dollar", "Cheaper than daily rental", "Monthly rates beat stacking daily fees for weeks on end."],
  ["check", "No ownership commitment", "Skip the loan, depreciation, and resale hassle entirely."],
  ["wrench", "Maintenance included", "We keep the car serviced, registered, and road-ready."],
];

export function LandingPage() {
  return (
    <div className="bg-paper-card text-ink">
      {/* dark graphite nav */}
      <div className="sticky top-0 z-40 bg-[#14141ae8] backdrop-blur border-b border-white/[0.08]">
        <nav className="max-w-6xl mx-auto px-7 py-4 flex items-center justify-between">
          <div className="flex items-center gap-9">
            <div className="flex items-center gap-2.5">
              <span className="h-[30px] w-[30px] rounded-[9px] bg-white grid place-items-center"><span className="h-[11px] w-[11px] rounded-[3px] bg-accent" /></span>
              <span className="text-white font-extrabold text-xl tracking-tight">FleetLease</span>
            </div>
            <div className="hidden md:flex items-center gap-7 text-[15px] font-semibold">
              <a href="#fleet" className="text-white/70 hover:text-white">Browse cars</a>
              <a href="#how" className="text-white/70 hover:text-white">How it works</a>
              <a href="#why" className="text-white/70 hover:text-white">Why monthly</a>
            </div>
          </div>
          <div className="flex items-center gap-3.5">
            <a href="/app/login" className="text-white text-[15px] font-semibold">Owner sign in</a>
            <a href="#fleet" className="bg-white text-ink text-[15px] font-bold px-5 py-2.5 rounded-xl hover:bg-white/90">Browse cars</a>
          </div>
        </nav>
      </div>

      {/* hero */}
      <div className="max-w-6xl mx-auto px-7 pt-10 pb-16 grid md:grid-cols-[1fr_1.05fr] gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-50 mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-ok" />
            <span className="text-accent text-[13px] font-bold">12 cars available this month</span>
          </div>
          <h1 className="text-[44px] md:text-6xl font-extrabold leading-[1.02] tracking-[-0.04em]">A better car,<br/>by the month.</h1>
          <p className="mt-5 text-ink-muted text-lg md:text-xl leading-relaxed max-w-md">Quality vehicles on simple monthly billing. Insurance, maintenance and registration included. Keep it as long as you like — cancel anytime.</p>

          {/* search bar */}
          <div className="mt-8 flex items-stretch gap-2 p-2 bg-paper-card border border-black/10 rounded-2xl shadow-soft max-w-lg">
            <div className="flex-[1.3] px-3.5 py-2">
              <div className="text-[11px] font-bold text-neutral uppercase tracking-wide">Pickup city</div>
              <div className="text-[15px] font-semibold mt-0.5">San Francisco, CA</div>
            </div>
            <div className="w-px bg-black/10" />
            <div className="flex-1 px-3.5 py-2">
              <div className="text-[11px] font-bold text-neutral uppercase tracking-wide">Term</div>
              <div className="text-[15px] font-semibold mt-0.5">3 months</div>
            </div>
            <a href="#fleet" className="shrink-0 grid place-items-center w-[50px] rounded-xl bg-accent hover:bg-accent-600">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            </a>
          </div>

          <div className="flex items-center gap-7 mt-7 text-[14px] font-semibold text-ink/70">
            <span className="inline-flex items-center gap-2"><Icon name="shield" size={19} /> Insurance included</span>
            <span className="inline-flex items-center gap-2"><Icon name="wrench" size={19} /> Maintenance covered</span>
          </div>
        </div>

        {/* hero photo placeholder + overlays */}
        <div className="relative">
          <ImageOrPrompt file="hero.png" prompt={HERO_PROMPT} className="w-full aspect-[5/4] rounded-[26px]" />
          <div className="absolute left-5 bottom-5 flex items-center gap-3 px-4 py-3 rounded-2xl bg-paper-card/95 backdrop-blur shadow-soft">
            <span className="h-[42px] w-[42px] rounded-xl bg-ink grid place-items-center text-white"><Icon name="car" size={22} /></span>
            <div><div className="text-[15px] font-bold">From $620 / month</div><div className="text-[13px] text-ink-muted">All-in. No hidden fees.</div></div>
          </div>
          <div className="absolute right-5 top-5 flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-paper-card/95 backdrop-blur shadow-soft">
            <Star /><span className="text-sm font-bold">4.96</span><span className="text-[13px] text-ink-muted">· 1,200+ trips</span>
          </div>
        </div>
      </div>

      {/* trust strip */}
      <div className="border-y border-black/[0.06] bg-paper">
        <div className="max-w-6xl mx-auto px-7 py-6 flex items-center justify-between flex-wrap gap-4">
          <span className="text-neutral text-[13px] font-bold tracking-wide">EVERYTHING ON ONE MONTHLY BILL</span>
          <div className="flex items-center gap-10 flex-wrap">
            {[["$0","to start"],["48 hrs","to keys"],["Mo-to-mo","cancel anytime"],["100%","maintenance"]].map(([a,b]) => (
              <div key={b} className="flex items-baseline gap-2"><span className="text-[22px] font-extrabold tracking-tight">{a}</span><span className="text-sm text-ink-muted">{b}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* fleet */}
      <div id="fleet" className="max-w-6xl mx-auto px-7 py-20">
        <div className="flex items-end justify-between flex-wrap gap-3.5 mb-9">
          <div>
            <h2 className="text-[38px] font-extrabold tracking-[-0.035em]">Available right now</h2>
            <p className="mt-3 text-ink-muted text-[17px]">Real cars, ready this week. Prices are all-in, per month.</p>
          </div>
          <a href="#lead" className="inline-flex items-center gap-1.5 font-bold text-[15px]">See all 12 cars →</a>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FLEET.map((c) => (
            <a key={c.name} href="#lead" className="group rounded-2xl overflow-hidden bg-paper-card border border-black/[0.07] hover:-translate-y-1 hover:shadow-soft transition-all">
              <div className="relative">
                <ImageOrPrompt file={c.img} prompt={c.prompt} className="w-full aspect-[3/2]" />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-paper-card/95 text-ok text-xs font-bold">Available now</span>
              </div>
              <div className="p-[18px]">
                <div className="flex items-center justify-between">
                  <div className="text-[17px] font-bold whitespace-nowrap">{c.name}</div>
                  <div className="flex items-center gap-1"><Star s={14} /><span className="text-sm font-bold">{c.rating}</span></div>
                </div>
                <div className="text-ink-muted text-sm mt-1.5">{c.meta}</div>
                <div className="flex items-baseline justify-between mt-4 pt-4 border-t border-black/[0.06]">
                  <div><span className="text-[22px] font-extrabold">{c.price}</span><span className="text-ink-muted text-sm"> /mo</span></div>
                  <span className="text-sm font-bold text-accent">Request →</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* how it works */}
      <div id="how" className="bg-paper border-y border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-7 py-20">
          <h2 className="text-[38px] font-extrabold tracking-[-0.035em] text-center">Keys in hand in four steps</h2>
          <p className="mt-3 text-ink-muted text-[17px] text-center max-w-md mx-auto">No dealership runaround, no mountain of paperwork. Pick, apply, drive.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {STEPS.map(([t, d], i) => (
              <div key={t} className="bg-paper-card border border-black/[0.07] rounded-2xl p-[26px]">
                <div className={`h-[42px] w-[42px] rounded-xl grid place-items-center text-white font-extrabold text-[17px] ${i === 3 ? "bg-accent" : "bg-ink"}`}>{i + 1}</div>
                <div className="font-bold text-[17px] mt-[18px]">{t}</div>
                <p className="text-ink-muted text-sm leading-relaxed mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* why monthly */}
      <div id="why" className="max-w-6xl mx-auto px-7 py-20 grid md:grid-cols-2 gap-14 items-center">
        <div>
          <h2 className="text-[38px] font-extrabold tracking-[-0.035em] leading-[1.08]">All the car.<br/>None of the baggage.</h2>
          <p className="mt-4 text-ink-muted text-[17px] leading-relaxed max-w-md">Skip the loan, the depreciation, and the resale headache. Pay one predictable rate and let us handle the rest.</p>
          <div className="grid gap-3.5 mt-7">
            {WHY.map(([ic, t, d]) => (
              <div key={t} className="flex gap-4 items-start">
                <span className="shrink-0 h-[42px] w-[42px] rounded-xl bg-accent-50 grid place-items-center text-accent"><Icon name={ic} size={20} width={2} /></span>
                <div><div className="font-bold text-base">{t}</div><p className="text-ink-muted text-sm leading-snug mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
        <ImageOrPrompt file="lifestyle.png" prompt={LIFESTYLE_PROMPT} className="w-full aspect-[4/3] rounded-3xl" />
      </div>

      {/* CTA band — near-black, with the live lead form */}
      <LeadForm />

      {/* footer */}
      <footer className="bg-ink border-t border-white/[0.08] py-9 px-7">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2.5">
            <span className="h-[26px] w-[26px] rounded-lg bg-white grid place-items-center"><span className="h-[9px] w-[9px] rounded-[2px] bg-accent" /></span>
            <span className="text-white font-bold">FleetLease</span>
            <span className="text-white/45 text-[13px] ml-2">© {new Date().getFullYear()} · Long-term car rentals, by the month.</span>
          </div>
          <div className="flex gap-6 text-sm">
            <a href="#fleet" className="text-white/65 hover:text-white">Fleet</a>
            <a href="#how" className="text-white/65 hover:text-white">How it works</a>
            <a href="/app/login" className="text-white/65 hover:text-white">Owner sign in</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PromptPlaceholder({ prompt, className }: { prompt: string; className?: string }) {
  return (
    <div className={`relative grid place-items-center bg-gradient-to-br from-ink-soft/10 to-accent/15 border border-black/5 overflow-hidden ${className ?? ""}`} aria-hidden>
      <div className="px-5 text-center max-w-[260px]">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-accent/80"><Icon name="image" size={13} /> AI image prompt</div>
        <p className="mt-1.5 text-[12px] leading-snug text-ink-muted">{prompt}</p>
      </div>
    </div>
  );
}

function ImageOrPrompt({ file, prompt, className = "" }: { file: string; prompt: string; className?: string }) {
  const [ok, setOk] = useState(true);
  if (!ok) return <PromptPlaceholder prompt={prompt} className={className} />;
  return <img src={`/cars/${file}`} alt="" className={`object-cover ${className}`} onError={() => setOk(false)} />;
}

type FormValues = import("@fleetlease/shared").LeadFormInput;

function LeadForm() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<FormValues>({ resolver: zodResolver(LeadFormInput) });

  const onSubmit = async (values: FormValues) => {
    await apiFetch("/public/leads", { method: "POST", body: JSON.stringify(values) });
    setSent(true);
  };

  return (
    <div id="lead" className="bg-ink">
      <div className="max-w-6xl mx-auto px-7 py-[70px] grid md:grid-cols-2 gap-14 items-center">
        <div>
          <h2 className="text-white text-[40px] font-extrabold tracking-[-0.035em] leading-[1.05]">Request a rental</h2>
          <p className="mt-4 text-white/65 text-[17px] leading-relaxed max-w-sm">Tell us what you need. We'll match you to a car and reply within a day — no pitch, just a real conversation.</p>
          <div className="flex items-center gap-2.5 mt-6"><Star s={18} /><span className="text-white/85 text-[15px] font-semibold">Rated 4.96 by 1,200+ drivers</span></div>
        </div>

        {sent ? (
          <div className="bg-paper-card rounded-[22px] p-8 text-center">
            <div className="text-2xl font-extrabold">Thanks — we'll be in touch shortly.</div>
            <p className="mt-2 text-ink-muted">Your request reached the owner directly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-paper-card rounded-[22px] p-[26px]" noValidate>
            <input {...register("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            <div className="grid grid-cols-2 gap-3.5">
              <LField label="Name" error={errors.name?.message}><input {...register("name")} placeholder="Jane Driver" className="lf" autoComplete="name" /></LField>
              <LField label="Phone" error={errors.phone?.message}><input {...register("phone")} placeholder="(555) 010-2233" className="lf" autoComplete="tel" /></LField>
            </div>
            <LField label="Email (optional)" error={errors.email?.message}><input {...register("email")} placeholder="jane@email.com" className="lf" autoComplete="email" /></LField>
            <LField label="Desired duration (months)" error={errors.desiredDurationMonths?.message}><input type="number" min={1} {...register("desiredDurationMonths", { valueAsNumber: true })} className="lf" /></LField>
            <LField label="Notes" error={errors.notes?.message}><textarea {...register("notes")} rows={2} placeholder="Which car caught your eye? Any timing needs?" className="lf resize-y" /></LField>
            <button disabled={isSubmitting}
              className="mt-4 w-full bg-ink text-white text-[15px] font-bold py-3.5 rounded-xl hover:bg-black disabled:opacity-50">
              {isSubmitting ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function LField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block mt-3.5 first:mt-0">
      <span className="text-[13px] font-semibold text-ink/80">{label}</span>
      <div className="mt-1.5 [&_.lf]:w-full [&_.lf]:rounded-xl [&_.lf]:border [&_.lf]:border-black/10 [&_.lf]:px-3.5 [&_.lf]:py-2.5 [&_.lf]:text-sm [&_.lf]:focus:outline-none [&_.lf]:focus:border-accent [&_.lf]:focus:ring-2 [&_.lf]:focus:ring-accent/15">
        {children}
      </div>
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
}
